import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { useLanguage } from "@/contexts/LanguageContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertTriangle,
  Calendar,
  MapPin,
  DollarSign,
  Shield,
  ChevronLeft,
  ExternalLink,
  Share2,
  Facebook,
  Newspaper,
  Globe,
  ZoomIn,
  X,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FraudCaseDetailPage({ user, onLogout, refreshUserData }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [fraudCase, setFraudCase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchCase();
  }, [slug]);

  const fetchCase = async () => {
    try {
      const response = await axios.get(`${API}/fraud-cases/public/${slug}`);
      setFraudCase(response.data);
    } catch (error) {
      console.error("Error fetching fraud case:", error);
      if (error.response?.status === 404) {
        navigate("/fraudes");
      }
    } finally {
      setLoading(false);
    }
  };

  const getRiskLevelInfo = (level) => {
    const info = {
      1: { color: "bg-green-100 text-green-800 border-green-300", label: "Risque faible", description: "Situation peu dangereuse" },
      2: { color: "bg-yellow-100 text-yellow-800 border-yellow-300", label: "Risque modéré", description: "Vigilance recommandée" },
      3: { color: "bg-orange-100 text-orange-800 border-orange-300", label: "Risque moyen", description: "Attention particulière requise" },
      4: { color: "bg-red-100 text-red-800 border-red-300", label: "Risque élevé", description: "Situation dangereuse" },
      5: { color: "bg-red-200 text-red-900 border-red-400", label: "Risque critique", description: "Danger immédiat" },
    };
    return info[level] || info[3];
  };

  const getSourceIcon = (sourceType) => {
    switch (sourceType) {
      case "JOURNAL":
        return <Newspaper className="h-5 w-5" />;
      case "FACEBOOK":
        return <Facebook className="h-5 w-5" />;
      default:
        return <Globe className="h-5 w-5" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("fr-CA", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatAmount = (amount) => {
    if (!amount) return null;
    return new Intl.NumberFormat("fr-CA", {
      style: "currency",
      currency: "CAD",
    }).format(amount);
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: fraudCase.title,
          text: fraudCase.excerpt || "Cas de fraude documenté sur Middleman",
          url: url,
        });
      } catch (err) {
        console.log("Share cancelled");
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Lien copié dans le presse-papier");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar user={user} onLogout={onLogout} refreshUserData={refreshUserData} />
        <div className="flex justify-center items-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        </div>
      </div>
    );
  }

  if (!fraudCase) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar user={user} onLogout={onLogout} refreshUserData={refreshUserData} />
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <AlertTriangle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-700 mb-2">Cas non trouvé</h1>
          <p className="text-gray-500 mb-6">Ce cas de fraude n'existe pas ou a été supprimé.</p>
          <Link to="/fraudes">
            <Button variant="outline" className="gap-2">
              <ChevronLeft className="h-4 w-4" />
              Retour aux cas de fraudes
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const riskInfo = getRiskLevelInfo(fraudCase.risk_level);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar user={user} onLogout={onLogout} refreshUserData={refreshUserData} />

      {/* Hero/Header */}
      <section className="bg-gradient-to-r from-slate-800 to-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link to="/fraudes" className="inline-flex items-center text-slate-300 hover:text-white mb-6 transition-colors">
            <ChevronLeft className="h-5 w-5 mr-1" />
            Tous les cas de fraudes
          </Link>

          <div className="flex flex-wrap items-start gap-4 mb-4">
            {fraudCase.risk_level && (
              <Badge className={`${riskInfo.color} border text-sm`}>
                <AlertTriangle className="h-4 w-4 mr-1" />
                {riskInfo.label}
              </Badge>
            )}
            {fraudCase.fraud_type && (
              <Badge variant="outline" className="border-slate-500 text-slate-300">
                {fraudCase.fraud_type.replace(/_/g, " ")}
              </Badge>
            )}
          </div>

          <h1 className="text-3xl sm:text-4xl font-bold mb-4">{fraudCase.title}</h1>

          <div className="flex flex-wrap gap-6 text-slate-300">
            {fraudCase.publish_date && (
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                {formatDate(fraudCase.publish_date)}
              </div>
            )}
            {fraudCase.location && (
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                {fraudCase.location}
              </div>
            )}
            {fraudCase.amount_lost && (
              <div className="flex items-center gap-2 text-red-400 font-semibold">
                <DollarSign className="h-5 w-5" />
                {formatAmount(fraudCase.amount_lost)} perdus
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Cover Image */}
              {fraudCase.cover_image_url && (
                <div className="mb-8 rounded-xl overflow-hidden shadow-lg">
                  <img
                    src={fraudCase.cover_image_url}
                    alt={fraudCase.title}
                    className="w-full h-auto"
                  />
                </div>
              )}

              {/* Excerpt */}
              {fraudCase.excerpt && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-8 rounded-r-lg">
                  <p className="text-red-800 text-lg italic">{fraudCase.excerpt}</p>
                </div>
              )}

              {/* Story */}
              {fraudCase.story && (
                <div className="prose prose-lg max-w-none mb-8">
                  <h2 className="text-2xl font-bold text-slate-800 mb-4">L'histoire</h2>
                  <div
                    className="text-slate-700 leading-relaxed whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(fraudCase.story.replace(/\n/g, "<br/>")) }}
                  />
                </div>
              )}

              {/* Gallery */}
              {fraudCase.images && fraudCase.images.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-slate-800 mb-4">Galerie de preuves</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {fraudCase.images.map((image, index) => (
                      <Dialog key={image.id || index}>
                        <DialogTrigger asChild>
                          <div className="relative group cursor-pointer rounded-lg overflow-hidden aspect-square">
                            <img
                              src={image.url}
                              alt={image.alt || `Image ${index + 1}`}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                              <ZoomIn className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          </div>
                        </DialogTrigger>
                        <DialogContent className="max-w-4xl p-0 bg-transparent border-0">
                          <div className="relative">
                            <img
                              src={image.url}
                              alt={image.alt || `Image ${index + 1}`}
                              className="w-full h-auto rounded-lg"
                            />
                          </div>
                        </DialogContent>
                      </Dialog>
                    ))}
                  </div>
                </div>
              )}

              {/* Source */}
              {fraudCase.source_url && (
                <div className="mt-8 p-6 bg-slate-100 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    {getSourceIcon(fraudCase.source_type)}
                    <span className="font-medium text-slate-700">
                      {fraudCase.source_name || "Source originale"}
                    </span>
                  </div>
                  <a
                    href={fraudCase.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {fraudCase.source_button_label || "Voir la source originale"}
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-8 space-y-6">
                {/* Risk Level Card */}
                {fraudCase.risk_level && (
                  <Card className={`border-2 ${riskInfo.color}`}>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <AlertTriangle className="h-8 w-8" />
                        <div>
                          <p className="font-bold text-lg">{riskInfo.label}</p>
                          <p className="text-sm opacity-80">{riskInfo.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Share */}
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-4">Partager ce cas</h3>
                    <Button onClick={handleShare} variant="outline" className="w-full gap-2">
                      <Share2 className="h-4 w-4" />
                      Partager
                    </Button>
                  </CardContent>
                </Card>

                {/* Tags */}
                {fraudCase.tags && fraudCase.tags.length > 0 && (
                  <Card>
                    <CardContent className="p-6">
                      <h3 className="font-semibold mb-4">Tags</h3>
                      <div className="flex flex-wrap gap-2">
                        {fraudCase.tags.map((tag, index) => (
                          <Badge key={index} variant="secondary">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* CTA */}
                <Card className="bg-gradient-to-br from-brand-600 to-brand-700 text-white border-0">
                  <CardContent className="p-6">
                    <Shield className="h-10 w-10 mb-4 opacity-80" />
                    <h3 className="font-bold text-lg mb-2">Protégez-vous</h3>
                    <p className="text-brand-100 text-sm mb-4">
                      Utilisez Middleman pour sécuriser vos transactions de billets.
                    </p>
                    <Link to="/auth">
                      <Button variant="secondary" className="w-full">
                        Commencer
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
